import { ApolloError, gql, useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import clientSimulator from "utils/clientSimulator";
import useNotifier from "hooks/useNotifier";
import { handleGQLErrors } from "utils/gqlErrors";
import {
  AvatarCsvExportStatus,
  AvatarCsvExportStatusVariables,
  StartAvatarCsvExport,
  StartAvatarCsvExportVariables,
} from "./types/AvatarCsvExport";

/** Avatar attr CSV export only: federated gateway (clientSimulator / FEDERATION_API), not thingMakeSimulator. */

const POLL_MS = 1500;
const MAX_POLLS = 800;

export const startAvatarCsvExportMutation = gql`
  mutation StartAvatarCsvExport($input: StartAvatarCsvExportInput!) {
    startAvatarCsvExport(input: $input) {
      exportId
    }
  }
`;

export const avatarCsvExportStatusQuery = gql`
  query AvatarCsvExportStatus($exportId: String!) {
    avatarCsvExportStatus(exportId: $exportId) {
      exportId
      status
      processedRows
      downloadUrl
      s3Key
      errorMessage
    }
  }
`;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useBatchAvatarCsvExport() {
  const notify = useNotifier();
  const [starting, setStarting] = useState(false);

  const [mutate] = useMutation<StartAvatarCsvExport, StartAvatarCsvExportVariables>(
    startAvatarCsvExportMutation,
    { client: clientSimulator }
  );

  const pollUntilDone = useCallback(
    async (exportId: string) => {
      for (let i = 0; i < MAX_POLLS; i++) {
        const { data, errors } = await clientSimulator.query<
          AvatarCsvExportStatus,
          AvatarCsvExportStatusVariables
        >({
          query: avatarCsvExportStatusQuery,
          variables: { exportId },
          fetchPolicy: "network-only",
        });
        if (errors?.length) {
          handleGQLErrors(
            notify,
            new ApolloError({ graphQLErrors: errors })
          );
          return;
        }
        const s = data?.avatarCsvExportStatus;
        if (!s) {
          notify({ status: "error", text: "Could not read export status" });
          return;
        }
        if (s.status === "COMPLETE" && s.downloadUrl) {
          window.open(s.downloadUrl, "_blank", "noopener,noreferrer");
          notify({
            status: "success",
            text: `Avatar attribute CSV ready (${s.processedRows ?? 0} rows)`,
          });
          return;
        }
        if (s.status === "FAILED") {
          notify({
            status: "error",
            text: s.errorMessage || "Avatar CSV export failed",
          });
          return;
        }
        await sleep(POLL_MS);
      }
      notify({ status: "error", text: "Avatar CSV export timed out" });
    },
    [notify]
  );

  const startExportForBatch = useCallback(
    async (batchId: string) => {
      setStarting(true);
      try {
        notify({
          status: "success",
          text: "Preparing avatar attribute CSV…",
        });
        const { data, errors } = await mutate({
          variables: { input: { batchId } },
        });
        if (errors?.length) {
          handleGQLErrors(
            notify,
            new ApolloError({ graphQLErrors: errors })
          );
          return;
        }
        const exportId = data?.startAvatarCsvExport?.exportId;
        if (!exportId) {
          notify({ status: "error", text: "Export did not return an id" });
          return;
        }
        await pollUntilDone(exportId);
      } catch (err: any) {
        if (err?.graphQLErrors || err?.networkError) {
          handleGQLErrors(notify, err);
        } else {
          notify({
            status: "error",
            text: err?.message || "Avatar CSV export failed",
          });
        }
      } finally {
        setStarting(false);
      }
    },
    [mutate, notify, pollUntilDone]
  );

  return { startExportForBatch, starting };
}
